import * as React from "react";
import { Auth, CognitoHostedUIIdentityProvider, CognitoUser } from "@aws-amplify/auth";
import { useParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import { useQuery } from "react-query";
import { LoadingPage } from "components/LoadingPage/LoadingPage";
import { useNavigation } from "../../hooks/useNavigation";
import { useUserSessionOverride } from "../../hooks/useUserSessionOverride";
import { ROUTES } from "../../utils/navigationUtils";
import { setInternalUserPoolSession } from "../../utils/amplifyConfigUtils";
import { CustomOAuthState } from "../../types/CustomOAuthState";
import { signIn as signInAction } from "../../store/userSession/userSessionActions";
import { UserAccountsService, UserGroup } from "../../gen/clients/llts";

interface PathParams {
  username?: string;
}

const InternalLoginPage: React.FC = () => {
  const { navigateTo } = useNavigation();
  const dispatch = useDispatch();
  const { username } = useParams<PathParams>();
  const [userSessionOverride, setUserSessionOverride, clearUserSessionOverride] = useUserSessionOverride();
  const [isAuthenticated, setIsAuthenticated] = React.useState(false);

  const { data: overrideUserAccount, error } = useQuery("getImpersonatedUser", {
    enabled: isAuthenticated && !!username && !!userSessionOverride,
    queryFn: UserAccountsService.getUserAccount,
    cacheTime: 0
  });

  React.useEffect(() => {
    async function signIn() {
      // If the user is already signed-in, impersonate the requested user or redirect to the home page.
      try {
        const user: CognitoUser = await Auth.currentAuthenticatedUser();
        // Impersonation is only allowed for the signed-in user's Admin/SuperUser groups.
        const cognitoGroups: (UserGroup | string)[] =
          user.getSignInUserSession()?.getIdToken().decodePayload()["cognito:groups"] || [];
        const isImpersonationAllowed =
          cognitoGroups.includes(UserGroup.ADMIN) || cognitoGroups.includes(UserGroup.SUPER_USER);
        if (username && isImpersonationAllowed) {
          setUserSessionOverride({ username });
          setIsAuthenticated(true);
        } else {
          navigateTo(ROUTES.HOME, true);
        }
      } catch (e) {
        // Otherwise sign-in against the internal user pool via hosted UI.
        setInternalUserPoolSession(true);
        // Preserve the impersonation path across the redirect so it can be applied on return.
        const customOAuthState: CustomOAuthState = {
          initialPagePath: username ? window.location.pathname : ROUTES.HOME
        };
        await Auth.federatedSignIn({
          customProvider: import.meta.env.VITE_INTERNAL_USERPOOL_IDP_PROVIDER_NAME,
          customState: JSON.stringify(customOAuthState)
        });
      }
    }

    signIn();
  }, [navigateTo, username, setUserSessionOverride]);

  React.useEffect(() => {
    if (!overrideUserAccount) {
      return;
    }
    dispatch(
      signInAction(
        overrideUserAccount.loginName,
        overrideUserAccount.email,
        overrideUserAccount.roles,
        undefined,
        undefined,
        undefined
      )
    );
    setUserSessionOverride({
      username: overrideUserAccount.loginName,
      email: overrideUserAccount.email,
      roles: overrideUserAccount.roles
    });
    navigateTo(ROUTES.HOME, true);
  }, [dispatch, navigateTo, overrideUserAccount, setUserSessionOverride]);

  React.useEffect(() => {
    if (error) {
      clearUserSessionOverride();
    }
  }, [clearUserSessionOverride, error]);

  return <LoadingPage />;
};

export { InternalLoginPage };
